import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/common/toast';
import { AsyncButton } from '@/components/common/AsyncButton';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { ApiError } from '@/api/client/abpError';
import { usePermission } from '@/permissions/usePermission';
import { portfoliosApi } from '@/api/services/onboarding';
import type {
  CreatePortfolioDto,
  PortfolioDatatableDto,
  PortfolioDto,
  PortfoliosDropdownDataDto,
} from '@/api/types/onboarding';
import { renderTruncatedList } from '../helpers';

const P = 'User.Onboarding.Portfolios';

interface Draft {
  name: string;
  groupTextFilter: string;
  groups: string[]; // Guid[]
  lPs: string[]; // int[] as string
  logins: string[]; // Guid[]
}

const emptyDraft = (): Draft => ({ name: '', groupTextFilter: '', groups: [], lPs: [], logins: [] });

export function PortfoliosTab() {
  const toast = useToast();
  const can = usePermission();
  const canCreate = can(`${P}.Create`);
  const canUpdate = can(`${P}.Update`);
  const canDelete = can(`${P}.Delete`);
  const canDownloadExcel = can(`${P}.DownloadExcel`);

  const dropdowns = useQuery({
    queryKey: ['onboarding', 'portfolios', 'dropdown'],
    queryFn: portfoliosApi.getDropdownData,
  });
  const opts = dropdowns.data;

  const [newDraft, setNewDraft] = useState<Draft | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PortfolioDto | null>(null);
  const [ddParent, setDdParent] = useState<HTMLElement | null>(null);

  const table = useServerDataTable<PortfolioDto, Partial<PortfolioDatatableDto>>({
    queryKey: ['onboarding', 'portfolios'],
    fetcher: (req) => portfoliosApi.getListForDataTable(req as PortfolioDatatableDto),
    pageSize: 5,
  });

  const groupOpts = (opts?.groups ?? []).map((g) => ({ value: g.id, label: g.name }));
  const lpOpts = (opts?.lPs ?? []).map((l) => ({ value: l.id, label: l.name }));
  const loginOpts = (opts?.logins ?? []).map((l) => ({ value: l.id, label: l.name }));

  // Build the LoginsIds Guid→dataSourceId dictionary from selected login Guids.
  const buildLoginsIds = (logins: string[], d: PortfoliosDropdownDataDto | undefined): Record<string, number> => {
    const dict: Record<string, number> = {};
    logins.forEach((g) => {
      const match = d?.logins.find((l) => l.id === g);
      if (match) dict[g] = match.dataSourceId;
    });
    return dict;
  };

  const buildPayload = (d: Draft): CreatePortfolioDto => ({
    name: d.name,
    groupTextFilter: d.groupTextFilter.trim() || null,
    groupsIds: d.groups,
    lPsIds: d.lPs.map((v) => Number.parseInt(v, 10)),
    loginsIds: buildLoginsIds(d.logins, opts),
  });

  const saveNew = async () => {
    if (!newDraft) return;
    if (!newDraft.name.trim()) {
      toast.error('Name is required. Please fill it in.');
      return;
    }
    setSaving(true);
    try {
      await portfoliosApi.create(buildPayload(newDraft));
      toast.success('New Portfolio Added Successfully');
      setNewDraft(null);
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to add portfolio.');
    } finally {
      setSaving(false);
    }
  };

  // The grid stores joined-name strings, not ids. For edit we start from empty
  // multi-selects (legacy re-selects from dropdown data); name/groupTextFilter prefill.
  const beginEdit = (row: PortfolioDto) => {
    setEditId(row.id);
    setEditDraft({
      name: row.name,
      groupTextFilter: row.groupTextFilter ?? '',
      groups: matchByNames(row.groups, groupOpts),
      lPs: matchByNames(row.lPs, lpOpts),
      logins: matchByNames(row.logins, loginOpts),
    });
  };

  // Resolve joined display names back to option values where they match.
  function matchByNames(joined: string, options: { value: string | number; label: string }[]): string[] {
    if (!joined) return [];
    const names = joined.split(',').map((s) => s.trim()).filter(Boolean);
    return names
      .map((n) => options.find((o) => o.label === n)?.value)
      .filter((v): v is string | number => v != null)
      .map(String);
  }

  const saveEdit = async () => {
    if (editId == null || !editDraft) return;
    if (!editDraft.name.trim()) {
      toast.error('Name is required. Please fill it in.');
      return;
    }
    setSaving(true);
    try {
      await portfoliosApi.update(editId, buildPayload(editDraft));
      toast.success('Saved successfully.');
      setEditId(null);
      setEditDraft(null);
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to update portfolio.');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await portfoliosApi.delete(deleteTarget.id);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to delete.');
    } finally {
      setDeleteTarget(null);
      table.refetch();
    }
  };

  const columns = useMemo<DataGridColumn<PortfolioDto>[]>(
    () => [
      {
        key: 'name', title: 'Name', className: 'text-start', value: (r) => r.name,
        cell: (r) => (editId === r.id && editDraft
          ? <input className="form-control form-control-sm" value={editDraft.name} disabled={saving} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} />
          : r.name),
      },
      {
        key: 'groups', title: 'Groups', sortable: false, className: 'text-start', value: (r) => r.groups,
        cell: (r) => (editId === r.id && editDraft
          ? <div style={{ minWidth: 200 }}><Select2Multi value={editDraft.groups} options={groupOpts} placeholder="Groups" disabled={saving} dropdownParent={ddParent} onChange={(v) => setEditDraft({ ...editDraft, groups: v })} /></div>
          : renderTruncatedList(r.groups, 3)),
      },
      {
        key: 'groupTextFilter', title: 'Group Text Filter', sortable: false, className: 'text-start', value: (r) => r.groupTextFilter,
        cell: (r) => (editId === r.id && editDraft
          ? <input className="form-control form-control-sm" value={editDraft.groupTextFilter} disabled={saving} onChange={(e) => setEditDraft({ ...editDraft, groupTextFilter: e.target.value })} />
          : r.groupTextFilter),
      },
      {
        key: 'lPs', title: 'LPs', sortable: false, className: 'text-start', value: (r) => r.lPs,
        cell: (r) => (editId === r.id && editDraft
          ? <div style={{ minWidth: 200 }}><Select2Multi value={editDraft.lPs} options={lpOpts} placeholder="LPs" disabled={saving} dropdownParent={ddParent} onChange={(v) => setEditDraft({ ...editDraft, lPs: v })} /></div>
          : renderTruncatedList(r.lPs, 3)),
      },
      {
        key: 'logins', title: 'Logins', sortable: false, filterable: false, className: 'text-start', value: (r) => r.logins,
        cell: (r) => (editId === r.id && editDraft
          ? <div style={{ minWidth: 200 }}><Select2Multi value={editDraft.logins} options={loginOpts} placeholder="Logins" disabled={saving} dropdownParent={ddParent} onChange={(v) => setEditDraft({ ...editDraft, logins: v })} /></div>
          : renderTruncatedList(r.logins, 3)),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editId, editDraft, saving, opts],
  );

  const toolbar = canCreate ? (
    <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-plus" title="Add new portfolio" onClick={() => { if (!newDraft) setNewDraft(emptyDraft()); }}>
      Add New
    </AsyncButton>
  ) : null;

  const draftRow = (d: Draft, set: (d: Draft) => void, onSave: () => void, onCancel: () => void) => (
    <tr className="onb-new-row">
      <td><input className="form-control form-control-sm" placeholder="Name" value={d.name} disabled={saving} onChange={(e) => set({ ...d, name: e.target.value })} /></td>
      <td style={{ minWidth: 200 }}><Select2Multi value={d.groups} options={groupOpts} placeholder="Groups" disabled={saving} dropdownParent={ddParent} onChange={(v) => set({ ...d, groups: v })} /></td>
      <td><input className="form-control form-control-sm" placeholder="Group Text Filter" value={d.groupTextFilter} disabled={saving} onChange={(e) => set({ ...d, groupTextFilter: e.target.value })} /></td>
      <td style={{ minWidth: 200 }}><Select2Multi value={d.lPs} options={lpOpts} placeholder="LPs" disabled={saving} dropdownParent={ddParent} onChange={(v) => set({ ...d, lPs: v })} /></td>
      <td style={{ minWidth: 200 }}><Select2Multi value={d.logins} options={loginOpts} placeholder="Logins" disabled={saving} dropdownParent={ddParent} onChange={(v) => set({ ...d, logins: v })} /></td>
      <td>
        <div className="d-flex gap-1">
          <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={onSave} disabled={saving} />
          <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={onCancel}><i className="fa fa-times" /></button>
        </div>
      </td>
    </tr>
  );

  const footerTop = newDraft ? draftRow(newDraft, setNewDraft, saveNew, () => setNewDraft(null)) : undefined;

  const rowActions = (canUpdate || canDelete)
    ? (row: PortfolioDto) => (
        <div className="d-flex gap-1">
          {canUpdate && editId !== row.id && (
            <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => beginEdit(row)}><i className="fa fa-pencil" /></button>
          )}
          {editId === row.id && (
            <>
              <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={saveEdit} disabled={saving} />
              <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={() => { setEditId(null); setEditDraft(null); }}><i className="fa fa-times" /></button>
            </>
          )}
          {canDelete && editId !== row.id && (
            <button type="button" className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => setDeleteTarget(row)}><i className="fa fa-trash" /></button>
          )}
        </div>
      )
    : undefined;

  return (
    <div ref={setDdParent}>
      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => String(r.id)}
        card
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={table.setColumnFilter}
        toolbarStart={toolbar}
        includeExcel={canDownloadExcel}
        excelFileName="Portfolios"
        footerTop={footerTop}
        rowActions={rowActions}
        rowClassName={(r) => (editId === r.id ? 'sfc-detail-editing' : undefined)}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data available'}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
      />

      <ConfirmDialog
        show={deleteTarget != null}
        message={deleteTarget ? `Delete portfolio '${deleteTarget.name}'?` : ''}
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
